import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Bell, BellOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../i18n';

// Convert base64 to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const PushNotificationManager = ({ variant = 'full' }) => {
  const { t } = useLanguage();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [vapidKey, setVapidKey] = useState('');
  const [permission, setPermission] = useState('default');

  // Check if push is supported and get current status
  const checkPushStatus = useCallback(async () => {
    setIsLoading(true);
    
    // Check browser support
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setIsSupported(false);
      setIsLoading(false);
      return;
    }
    
    setIsSupported(true);
    setPermission(Notification.permission);
    
    try {
      // Get VAPID key from server
      const { data } = await api.getVapidKey();
      if (data.configured && data.public_key) {
        setVapidKey(data.public_key);
      }
      
      // Check if already subscribed
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking push status:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkPushStatus();
  }, [checkPushStatus]);

  const subscribeToPush = async () => {
    setIsLoading(true);
    
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      setPermission(permission);
      
      if (permission !== 'granted') {
        toast.error(t('pushManager.toast.permissionDenied'));
        setIsLoading(false);
        return;
      }
      
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push
      let subscription;
      if (vapidKey) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        });
      } else {
        // Without VAPID key, create a simple subscription for demo
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true
        });
      }
      
      // Send subscription to server
      const subscriptionJson = subscription.toJSON();
      await api.subscribePush({
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys || {},
        user_agent: navigator.userAgent
      });
      
      setIsSubscribed(true);
      toast.success(t('pushManager.toast.enabled'));
    } catch (error) {
      console.error('Error subscribing to push:', error);
      toast.error(t('pushManager.toast.enableError'));
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setIsLoading(true);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();
        
        // Remove from server
        await api.unsubscribePush(subscription.endpoint);
      }
      
      setIsSubscribed(false);
      toast.success(t('pushManager.toast.disabled'));
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error(t('pushManager.toast.disableError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = () => {
    if (isSubscribed) {
      unsubscribeFromPush();
    } else {
      subscribeToPush();
    }
  };

  const testNotification = async () => {
    try {
      await api.testPush();
      toast.success(t('pushManager.toast.testSent'));
    } catch (error) {
      toast.error(t('pushManager.toast.testError'));
    }
  };

  // Compact variant for settings
  if (variant === 'compact') {
    return (
      <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <Bell className="h-5 w-5 text-orange-500" />
          ) : (
            <BellOff className="h-5 w-5 text-stone-400" />
          )}
          <div>
            <p className="font-medium text-stone-900">{t('pushManager.title')}</p>
            <p className="text-sm text-stone-500">
              {!isSupported 
                ? t('pushManager.compact.notSupported')
                : isSubscribed 
                  ? t('pushManager.compact.enabled')
                  : t('pushManager.compact.disabled')}
            </p>
          </div>
        </div>
        <Switch
          checked={isSubscribed}
          onCheckedChange={handleToggle}
          disabled={!isSupported || isLoading}
        />
      </div>
    );
  }

  // Full variant with card
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-orange-500" />
          {t('pushManager.title')}
        </CardTitle>
        <CardDescription>
          {t('pushManager.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isSupported ? (
          <div className="flex items-center gap-3 p-4 bg-stone-100 rounded-lg">
            <AlertCircle className="h-5 w-5 text-stone-500" />
            <p className="text-sm text-stone-600">
              {t('pushManager.notSupportedMessage')}
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{t('pushManager.enableTitle')}</p>
                <p className="text-sm text-stone-500">
                  {t('pushManager.enableDescription')}
                </p>
              </div>
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
              ) : (
                <Switch
                  checked={isSubscribed}
                  onCheckedChange={handleToggle}
                  data-testid="push-notification-toggle"
                />
              )}
            </div>
            
            {isSubscribed && (
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  {t('pushManager.enabledMessage')}
                </div>
                
                <div className="space-y-2">
                  <p className="text-sm font-medium text-stone-700">{t('pushManager.notificationTypesTitle')}</p>
                  <ul className="text-sm text-stone-600 space-y-1">
                    <li>{t('pushManager.notificationTypes.reminders')}</li>
                    <li>{t('pushManager.notificationTypes.paymentConfirmation')}</li>
                    <li>{t('pushManager.notificationTypes.contributionRecorded')}</li>
                    <li>{t('pushManager.notificationTypes.newMember')}</li>
                    <li>{t('pushManager.notificationTypes.rotation')}</li>
                    <li>{t('pushManager.notificationTypes.referralBonus')}</li>
                    <li>{t('pushManager.notificationTypes.tontineInvite')}</li>
                    <li>{t('pushManager.notificationTypes.catchupPlan')}</li>
                    <li>{t('pushManager.notificationTypes.importantAlerts')}</li>
                  </ul>
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={testNotification}
                  className="w-full"
                >
                  {t('pushManager.sendTest')}
                </Button>
              </div>
            )}
            
            {permission === 'denied' && (
              <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <div>
                  <p className="text-sm font-medium text-red-700">{t('pushManager.permissionDeniedTitle')}</p>
                  <p className="text-sm text-red-600">
                    {t('pushManager.permissionDeniedDescription')}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PushNotificationManager;
